import { ChangeDetectionStrategy, Component, ContentChild, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { WidthPreset } from '@vendasta/galaxy/page';
import { CommentInterface, CommentType } from '@vendasta/task-manager';
import { BehaviorSubject } from 'rxjs';
import { MAX_COMMENT_LENGTH } from '../shared/constants';
import { AddCommentEvent, ExtendedCommentInterface, ProjectTrackerUserDetails } from '../shared/interfaces';
import { ProjectCommentsInputDirective } from './project-comments.directives';

export interface CommentViewConfig {
  showTitle?: boolean;
  showCloseButton?: boolean;
  /**
   * When true, the comment input box will be shown above the comment list, and they will be sorted in reverse
   * chronological order. When false, the comment input box will be shown below the comment list, and they will be
   * sorted in chronological order.
   */
  showCommentInputAtTop?: boolean;
  /**
   * Whether or not to show the avatar icon of the user next to the input box
   */
  showCommenterIcon?: boolean;
}

@Component({
  selector: 'project-tracker-project-comments',
  templateUrl: './project-comments.component.html',
  styleUrls: ['./project-comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectCommentsComponent {
  @Input() activeUser: ProjectTrackerUserDetails;
  @Input()
  set conversation(value: ExtendedCommentInterface[]) {
    if (this.viewConfig?.showCommentInputAtTop) {
      value.sort((a, b) => b.messageCreated.getTime() - a.messageCreated.getTime());
    } else {
      value.sort((a, b) => a.messageCreated.getTime() - b.messageCreated.getTime());
    }
    this.conversation$$.next(value || []);
  }
  @Input() isLoading = false;
  @Input() isSubmitting = false;
  @Input() disablePrivate = true;
  @Input() disableSubmit = false;
  @Input() accountName: string;

  @Input() viewConfig?: CommentViewConfig = {
    showTitle: true,
    showCloseButton: true,
    showCommentInputAtTop: true,
    showCommenterIcon: true,
  };

  @Output() closeClicked: EventEmitter<any> = new EventEmitter();
  @Output() submitClicked: EventEmitter<AddCommentEvent> = new EventEmitter();

  @ContentChild(ProjectCommentsInputDirective) inputDirective: ProjectCommentsInputDirective;

  pageWidth = WidthPreset.Full;

  constructor(private translateService: TranslateService) {}

  get commentType(): typeof CommentType {
    return CommentType;
  }

  isPublic = false;
  conversation$$: BehaviorSubject<ExtendedCommentInterface[]> = new BehaviorSubject<ExtendedCommentInterface[]>([]);
  readonly newCommentInput = new UntypedFormControl(null, { updateOn: 'change' });

  get hasCustomInput(): boolean {
    return !!this.inputDirective;
  }

  get shouldDisableSubmit(): boolean {
    if (this.hasCustomInput) {
      return this.disableSubmit;
    }
    return !!this.disableSubmit || !this.isValidComment(this.newCommentInput?.value);
  }

  private isValidComment(text: string): boolean {
    return text?.trim()?.length > 0 && text?.trim()?.length <= MAX_COMMENT_LENGTH;
  }

  toggleIsPublic(): void {
    this.isPublic = !this.isPublic;
  }

  handleSubmitClick(): void {
    this.submitClicked.emit({
      message: this.newCommentInput?.value?.trim(),
      isPublic: this.disablePrivate ? true : this.isPublic,
    });
  }

  handleCloseClick(): void {
    this.closeClicked.emit();
  }

  clearCommentInput(): void {
    this.newCommentInput?.setValue('');
  }

  getConversationOrigin(conversation: CommentInterface): string {
    if (conversation?.senderName) {
      return conversation.senderName;
    } else if (this.isConversationHydrated(conversation)) {
      const fromText = this.translateService.instant('TASK_DETAILS.CONVERSATIONS_FROM');
      return conversation?.senderId.replace('|', fromText) || '';
    } else {
      return '';
    }
  }

  isConversationHydrated(conversation: CommentInterface): boolean {
    return !(conversation?.senderId?.startsWith('U-') || conversation?.senderId?.startsWith('UID-'));
  }
}
